// PartBPage2.jsx - Secondary Stage Part B Page 2 (Stage 1 Student Reflection) for HPC Report
import React from 'react';

const PartBPage2 = ({ data }) => {
  const studentData = data || {};

  const awarenessOptions = [
    { key: 'understand_purpose', label: 'I understand the purpose of the project.' },
    { key: 'read_understand_material', label: 'I could read and understand the resource material.' },
    { key: 'talk_about_things_known', label: 'I talk about things I know that are needed for the project.' },
    { key: 'identify_challenges', label: 'I identify challenges my group might face during the project.' },
    { key: 'enumerate_steps', label: 'I could enumerate and describe the steps (start to finish) required to do the project.' }
  ];

  const sensitivityOptions = [
    { key: 'listen_respect_ideas', label: 'I listen to my group\'s ideas and respect them.' },
    { key: 'collective_decisions', label: 'I try to make sure group decisions are taken collectively.' },
    { key: 'understand_all_aspects', label: 'I try to make sure that my peers understand all aspects of the project.' },
    { key: 'relate_to_objectives', label: 'I can meaningfully relate to the objectives of the project.' },
    { key: 'feel_joyous_contributing', label: 'I feel joyous in contributing to the project.' }
  ];

  const creativityOptions = [
    { key: 'different_ways_approach', label: 'I think of different ways to approach the task.' },
    { key: 'brainstorm_execution', label: 'I brainstorm about project execution and presentation.' },
    { key: 'think_different_resources', label: 'I think of different resources to be used in the project.' },
    { key: 'innovative_solutions', label: 'I come up with innovative solutions to mitigate the challenges.' },
    { key: 'relate_output_daily_life', label: 'I can think of new ideas to relate the output of the project in daily life.' }
  ];

  const calculateTotal = (category) => {
    const data = studentData[category] || {};
    return Object.values(data).filter(Boolean).length;
  };

  const renderText = (text) => {
    if (!text || text.trim() === '') return <div style={{ fontSize: '9px', color: '#999' }}>-</div>;
    return <div style={{ fontSize: '9px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{text}</div>;
  };

  const renderList = (items) => {
    if (!items || items.length === 0) return <div style={{ fontSize: '9px', color: '#999' }}>-</div>;
    return (
      <div style={{ fontSize: '9px', lineHeight: '1.6' }}>
        {items.map((item, index) => (
          <div key={index} style={{ marginBottom: '2px' }}>• {item}</div>
        ))}
      </div>
    );
  };

  const renderCheckboxSection = (title, options, category, bgColor) => {
    const total = calculateTotal(category);

    return (
      <div style={{ marginBottom: '12px' }}>
        <div style={{
          backgroundColor: bgColor,
          color: 'white',
          padding: '6px 10px',
          fontWeight: 'bold',
          fontSize: '10px',
          marginBottom: '6px'
        }}>
          {title}
        </div>
        <div style={{
          backgroundColor: '#C5E3ED',
          padding: '8px 10px'
        }}>
          {options.map(option => (
            <div key={option.key} style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '4px',
              fontSize: '9px',
              lineHeight: '1.4'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                border: '1.5px solid #333',
                marginRight: '6px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '1px',
                fontSize: '10px'
              }}>
                {studentData[category]?.[option.key] && '✓'}
              </div>
              <span>{option.label}</span>
            </div>
          ))}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '6px',
            fontSize: '9px',
            fontWeight: 'bold'
          }}>
            <span style={{ marginRight: '8px' }}>Total number of (✓):</span>
            <div style={{
              width: '24px',
              height: '24px',
              border: '2px solid #333',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '10px'
            }}>
              {total}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="report-page" style={{
      width: '210mm',
      minHeight: '297mm',
      padding: '20mm',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      fontFamily: 'Arial, sans-serif',
      position: 'relative'
    }}>
      {/* Decorative Corners */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '0 80px 80px 0',
        borderColor: 'transparent #F4824C transparent transparent'
      }} />

      {/* Header */}
      <div style={{
        backgroundColor: '#F4824C',
        color: 'white',
        fontSize: '13px',
        fontWeight: 'bold',
        padding: '8px',
        textAlign: 'center',
        marginBottom: '12px'
      }}>
        Stage 1
      </div>

      {/* Project Schedule */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '12px',
            fontWeight: 'bold',
            fontSize: '10px',
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '100px'
          }}>
            Project schedule
          </div>
          <div style={{
            backgroundColor: '#C5E3ED',
            padding: '8px 12px',
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '6px',
            fontSize: '9px'
          }}>
            {[1, 2, 3, 4, 5].map(day => (
              <div key={day} style={{ display: 'flex', gap: '4px' }}>
                <strong style={{ minWidth: '40px' }}>Day {day}:</strong>
                <span>{studentData.project_schedule?.[`day_${day}`] || '-'}</span>
              </div>
            ))}
            {[6, 7, 8, 9, 10].map(day => (
              <div key={day} style={{ display: 'flex', gap: '4px' }}>
                <strong style={{ minWidth: '40px' }}>Day {day}:</strong>
                <span>{studentData.project_schedule?.[`day_${day}`] || '-'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '0.85fr 1fr 1fr',
        gap: '10px',
        marginBottom: '12px'
      }}>
        {/* Resources needed - Note pad style */}
        <div>
          <div style={{
            backgroundColor: '#8B6F47',
            color: '#F5E6D3',
            padding: '8px',
            fontWeight: 'bold',
            fontSize: '10px',
            textAlign: 'center',
            marginBottom: '4px',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px'
          }}>
            Resources needed
          </div>
          <div style={{
            backgroundColor: '#F5E6D3',
            padding: '8px',
            minHeight: '140px',
            borderLeft: '1px solid #8B6F47',
            borderRight: '1px solid #8B6F47',
            borderBottom: '2px solid #8B6F47',
            position: 'relative'
          }}>
            {/* Notepad lines */}
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{
                borderBottom: '1px solid #D4C4B0',
                height: '16px',
                marginBottom: '2px'
              }} />
            ))}
            <div style={{ position: 'absolute', top: '8px', left: '8px', right: '8px' }}>
              {renderList(studentData.resources_needed)}
            </div>
          </div>
        </div>

        {/* Roles of group members */}
        <div>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '8px',
            fontWeight: 'bold',
            fontSize: '10px',
            textAlign: 'center',
            marginBottom: '4px'
          }}>
            Roles of group members
          </div>
          <div style={{
            backgroundColor: '#C5E3ED',
            padding: '8px',
            minHeight: '140px',
            lineHeight: '1.5'
          }}>
            {renderText(studentData.roles_of_group_members)}
          </div>
        </div>

        {/* Possible barriers */}
        <div>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '8px',
            fontWeight: 'bold',
            fontSize: '10px',
            textAlign: 'center',
            marginBottom: '4px'
          }}>
            Possible barriers to doing the project
          </div>
          <div style={{
            backgroundColor: '#C5E3ED',
            padding: '8px',
            minHeight: '140px'
          }}>
            {renderList(studentData.possible_barriers)}
          </div>
        </div>
      </div>

      {/* Learner Reflection Section */}
      <div style={{
        backgroundColor: '#4A90A4',
        color: 'white',
        padding: '8px',
        fontWeight: 'bold',
        fontSize: '11px',
        marginBottom: '8px',
        textAlign: 'center'
      }}>
        Learner Reflection
      </div>

      <div style={{
        fontSize: '9px',
        marginBottom: '10px',
        fontWeight: 'bold'
      }}>
        Tick (✓) all statements that apply.
      </div>

      {renderCheckboxSection('Awareness', awarenessOptions, 'awareness', '#4A90A4')}
      {renderCheckboxSection('Sensitivity', sensitivityOptions, 'sensitivity', '#4A90A4')}
      {renderCheckboxSection('Creativity', creativityOptions, 'creativity', '#4A90A4')}

      {/* Page Number */}
      <div style={{
        position: 'absolute',
        bottom: '15mm',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '10px',
        color: '#666'
      }}>
        12
      </div>

      {/* Bottom decorative corners */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 0 0 60px',
        borderColor: 'transparent transparent transparent #F4824C'
      }} />
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 60px 0 0',
        borderColor: 'transparent #F4824C transparent transparent',
        opacity: 0.3
      }} />
    </div>
  );
};

export default PartBPage2;
